import { useEffect, useRef, useState } from "react"
import { resolveAuthHeaders } from "@/components/api/auth-strategy"

/**
 * Result returned by the `useFetchPdfBlob` hook.
 */
export type UseFetchPdfBlobResult = {
  blobUrl: string | null
  isLoading: boolean
  error: string | null
}

/**
 * Hook that fetches a PDF as a blob URL.
 *
 * Extracts the fetch logic from the legacy `openPresignedPdf` utility but
 * does NOT open a new browser window. Instead it returns a blob URL that
 * can be rendered inside an iframe or embed element.
 *
 * The hook automatically revokes the previous blob URL when `staticUrl`
 * changes or when the component unmounts, preventing memory leaks.
 *
 * @param params.staticUrl - The URL to fetch the presigned URL from, or
 *   `null` to reset to the initial idle state (no fetch).
 */
export function useFetchPdfBlob({
  staticUrl,
}: {
  staticUrl: string | null
}): UseFetchPdfBlobResult {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const blobUrlRef = useRef<string | null>(null)

  useEffect(() => {
    if (!staticUrl) {
      revokePreviousBlob()
      setBlobUrl(null)
      setIsLoading(false)
      setError(null)
      return
    }

    let cancelled = false

    async function fetchPdf(url: string) {
      revokePreviousBlob()
      setBlobUrl(null)
      setIsLoading(true)
      setError(null)

      try {
        const headers = await resolveAuthHeaders({ url, method: "GET" })
        const response = await fetch(url, { headers })

        if (!response.ok) {
          throw new Error(
            `Failed to fetch presigned URL: status ${response.status}`,
          )
        }

        const data = await response.json()

        if (!data?.presigned_url) {
          throw new Error("Response missing presigned_url field")
        }

        try {
          const parsed = new URL(data.presigned_url as string)
          if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
            throw new Error(
              `Unsafe presigned URL protocol: ${parsed.protocol}`,
            )
          }
        } catch (urlError) {
          if (
            urlError instanceof Error &&
            urlError.message.startsWith("Unsafe")
          ) {
            throw urlError
          }
          throw new Error("Invalid presigned URL format")
        }

        const pdfResponse = await fetch(data.presigned_url as string)

        if (!pdfResponse.ok) {
          throw new Error(
            `Failed to fetch PDF content: status ${pdfResponse.status}`,
          )
        }

        const rawBlob = await pdfResponse.blob()
        const pdfBlob = new Blob([rawBlob], { type: "application/pdf" })
        const newBlobUrl = URL.createObjectURL(pdfBlob)

        if (cancelled) {
          URL.revokeObjectURL(newBlobUrl)
          return
        }

        blobUrlRef.current = newBlobUrl
        setBlobUrl(newBlobUrl)
        setIsLoading(false)
      } catch (err) {
        if (cancelled) return
        const message =
          err instanceof Error ? err.message : "Error fetching PDF"
        console.error("Error fetching presigned PDF URL", err)
        setError(message)
        setIsLoading(false)
        setBlobUrl(null)
      }
    }

    void fetchPdf(staticUrl)

    return () => {
      cancelled = true
    }
  }, [staticUrl])

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }
  }, [])

  return { blobUrl, isLoading, error }

  function revokePreviousBlob() {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }
  }
}
